import type { AutoFillCondition, AutoFillConditionRule } from '../config/types';

const normalize = (value: unknown): string => String(value ?? '').trim();

const resolveFieldValue = (values: Record<string, unknown>, field: string): unknown => {
  if (Object.prototype.hasOwnProperty.call(values, field)) {
    return values[field];
  }

  const matchedKey = Object.keys(values).find((key) => key.endsWith(field));
  return matchedKey ? values[matchedKey] : undefined;
};

const parseDateValue = (value: unknown): number | null => {
  const text = normalize(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return null;

  const time = new Date(`${text}T00:00:00Z`).getTime();
  return Number.isFinite(time) ? time : null;
};

const evaluateRule = (rule: AutoFillConditionRule, values: Record<string, unknown>): boolean => {
  const rawValue = resolveFieldValue(values, rule.field);
  const value = normalize(rawValue);

  switch (rule.operator) {
    case 'notEmpty':
      return value.length > 0;
    case 'equals':
      return value === normalize(rule.value);
    case 'equalsIgnoreCase':
      return value.toLowerCase() === normalize(rule.value).toLowerCase();
    case 'numberGte': {
      const numericValue = Number(value.replace(/,/g, ''));
      const numericRuleValue = Number(rule.value);
      return (
        Number.isFinite(numericValue) &&
        Number.isFinite(numericRuleValue) &&
        numericValue >= numericRuleValue
      );
    }
    case 'dateGte': {
      const parsedValue = parseDateValue(value);
      const parsedRuleValue = parseDateValue(rule.value);
      return parsedValue !== null && parsedRuleValue !== null && parsedValue >= parsedRuleValue;
    }
    case 'dateLte': {
      const parsedValue = parseDateValue(value);
      const parsedRuleValue = parseDateValue(rule.value);
      return parsedValue !== null && parsedRuleValue !== null && parsedValue <= parsedRuleValue;
    }
    default:
      return false;
  }
};

export const evaluateAutoFillCondition = (
  autoFillCondition: AutoFillCondition,
  values: Record<string, unknown>,
): string =>
  autoFillCondition.conditions.every((condition) => evaluateRule(condition, values))
    ? autoFillCondition.trueValue
    : autoFillCondition.falseValue;
